
import { supabase } from "@/integrations/supabase/client";
import { logClaudeApiInteraction } from "../logging/claudeApiLogger";
import { getWordOptions, selectUnusedWord, getFallbackWord } from "../claude";
import { handleFallbackWords } from "./fallbackWordHandler";

/**
 * Get AI response for generating the next word
 */
export const getAiResponse = async (userWord: string, botWord: string, usedWordsArray: string[], gameId?: string, roundNumber?: number) => {
  try {
    console.log(`Requesting AI response for words: "${userWord}" and "${botWord}"`);
    console.log(`Used words count: ${usedWordsArray.length}`);
    
    // Get word options from Claude with multiple attempts
    let attempts = 0;
    const maxAttempts = 3;
    let wordOptions = [];
    let aiError = null;
    
    while (attempts < maxAttempts && wordOptions.length === 0) {
      attempts++;
      console.log(`Attempt ${attempts} of ${maxAttempts} to get word options`);
      
      try {
        // Pass the full list of used words to the API along with game info for logging
        wordOptions = await getWordOptions(userWord, botWord, usedWordsArray, gameId, roundNumber);
        
        if (wordOptions.length === 0 && attempts < maxAttempts) {
          console.log(`No word options returned on attempt ${attempts}, retrying...`);
          // Log empty response issue
          await logClaudeApiInteraction(
            gameId || null,
            roundNumber || null,
            'word_options',
            { userWord, botWord, usedWords: usedWordsArray },
            { wordOptions: [] },
            new Error("Empty word options array"),
            400,
            null,
            true,
            `Empty word options array (attempt ${attempts})`
          );
          
          // Retry after a short delay
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`Error getting word options (attempt ${attempts}):`, error);
        aiError = error;
        
        if (attempts < maxAttempts) {
          console.log(`Retrying after error on attempt ${attempts}...`);
          // Retry after a short delay
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          console.warn(`Max attempts (${maxAttempts}) reached with errors, trying random word from database`);
        }
      }
    }
    
    // If no word options after all attempts, try getting a random word from the database
    if (wordOptions.length === 0) {
      console.log("No word options returned after all attempts, trying random word from database");
      try {
        // Try to get a random word from the database using the RLS bypass function
        const { data: randomWord, error: randomWordError } = await supabase.rpc(
          'get_random_unused_word_bypass_rls',
          { used_words: usedWordsArray.map(w => w.toLowerCase()) }
        );
        
        if (randomWordError) {
          console.error("Error getting random word:", randomWordError);
          throw randomWordError;
        }
        
        if (randomWord) {
          console.log(`Got random word from database: ${randomWord}`);
          
          await logClaudeApiInteraction(
            gameId || null,
            roundNumber || null,
            'word_options',
            { userWord, botWord, usedWords: usedWordsArray },
            { randomWord },
            aiError,
            200,
            null,
            true,
            "Using random word from database after Claude failed"
          );
          
          return {
            nextAiWord: randomWord,
            explanation: "I chose a random word because I couldn't think of a good connection.",
            proximity: 40 // Default proximity for random words
          };
        }
      } catch (randomError) {
        console.error("Error with random word fallback:", randomError);
        // If random word fails, continue to fallback words
      }
      
      return handleFallbackWords(usedWordsArray, aiError, gameId, roundNumber);
    }
    
    console.log(`Received ${wordOptions.length} word options after ${attempts} attempts`);
    
    // Find an unused word option
    const selectedOption = selectUnusedWord(wordOptions, usedWordsArray);
    
    // If all options are used, fall back to a default word
    if (!selectedOption) {
      console.warn("All suggested words have already been used! Using fallback word.");
      
      await logClaudeApiInteraction(
        gameId || null,
        roundNumber || null,
        'word_options',
        { userWord, botWord, usedWords: usedWordsArray },
        { wordOptions },
        new Error("All suggested words already used"),
        200,
        null,
        true,
        "All suggested words have already been used"
      );
      
      return handleFallbackWords(usedWordsArray, new Error("All suggested words already used"), gameId, roundNumber);
    }
    
    console.log(`Final selected word: "${selectedOption.word}" with explanation: "${selectedOption.explanation}"`);
    
    return { 
      nextAiWord: selectedOption.word,
      explanation: selectedOption.explanation,
      proximity: 50 // Proximity will be calculated separately now
    };
  } catch (error) {
    console.error("Error getting AI response:", error);
    return handleFallbackWords(usedWordsArray, error, gameId, roundNumber);
  }
};
